import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { useAuth } from "../contexts/AuthContext";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/uploadData.css";
import { Project, projectService } from "../services/projectService";
import { getToken } from "../utils/authStorage";

const UploadData: React.FC = () => {
  const { user } = useAuth();
  const { isHiddenModeActive } = useHiddenMode();
  const [activeTab, setActiveTab] = useState<
    "booking" | "unit" | "income" | "expense" | "otherIncome"
  >("booking");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Memoized error grouping for analysis view
  const errorGroups = React.useMemo(() => {
    if (
      !uploadResult ||
      !uploadResult.errors ||
      uploadResult.errors.length === 0
    )
      return [] as Array<{
        message: string;
        count: number;
        sampleUnits: string[];
        sheetTypes: string[];
        rows: any[];
      }>;
    const map = new Map<
      string,
      {
        count: number;
        units: Set<string>;
        sheetTypes: Set<string>;
        rows: any[];
      }
    >();
    for (const e of uploadResult.errors as any[]) {
      const key = (e.error || e.message || "Unknown error").toString();
      if (!map.has(key)) {
        map.set(key, {
          count: 0,
          units: new Set<string>(),
          sheetTypes: new Set<string>(),
          rows: [],
        });
      }
      const entry = map.get(key)!;
      entry.count += 1;
      if (e?.data?.unitNo) entry.units.add(String(e.data.unitNo));
      if (e?.data?.sheetType) entry.sheetTypes.add(String(e.data.sheetType));
      if (entry.rows.length < 25) entry.rows.push(e); // keep some samples
    }
    return Array.from(map.entries())
      .map(([message, info]) => ({
        message,
        count: info.count,
        sampleUnits: Array.from(info.units).slice(0, 10),
        sheetTypes: Array.from(info.sheetTypes),
        rows: info.rows,
      }))
      .sort((a, b) => b.count - a.count);
  }, [uploadResult]);

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const projectsData = await projectService.getProjects(true); // Get only active projects
      setProjects(projectsData);

      // Auto-select first project if available
      if (projectsData.length > 0) {
        setSelectedProject(projectsData[0].id.toString());
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      setError("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  // Redirect if not in hidden mode
  if (!isHiddenModeActive) {
    return (
      <div className="unauthorized-access">
        <div className="unauthorized-content">
          <h2>🔒 Access Denied</h2>
          <p>This feature requires hidden mode access.</p>
          <p>Please enable hidden mode to continue.</p>
        </div>
      </div>
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "application/vnd.ms-excel", // .xls
        "text/csv", // .csv
      ];

      if (validTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        setError(null);
        setUploadResult(null);
      } else {
        setError("Please select a valid Excel file (.xlsx, .xls) or CSV file");
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    if (activeTab === "booking" && !selectedProject) {
      setError("Please select a project for booking upload");
      return;
    }

    if (activeTab === "income" && !selectedProject) {
      setError("Please select a project for income upload");
      return;
    }

    if (activeTab === "expense" && !selectedProject) {
      setError("Please select a project for expense upload");
      return;
    }

    if (activeTab === "otherIncome" && !selectedProject) {
      setError("Please select a project for other income upload");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setUploadResult(null);

      // ✅ NEW: For other income tab, parse Excel file
      if (activeTab === "otherIncome") {
        console.log("🚀 [OTHER INCOME UPLOAD] Starting other income upload process...");
        console.log("📁 [OTHER INCOME UPLOAD] File:", file.name);
        console.log("🏢 [OTHER INCOME UPLOAD] Selected Project ID:", selectedProject);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("projectId", selectedProject);

        const API_BASE_URL =
          process.env.REACT_APP_API_URL || "http://localhost:5010/api";
        const token = getToken();

        console.log(
          "🌐 [OTHER INCOME UPLOAD] API Endpoint:",
          `${API_BASE_URL}/upload-data/other-income`
        );

        const response = await fetch(`${API_BASE_URL}/upload-data/other-income`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "x-hidden-mode": "true",
          },
          body: formData,
        });

        console.log("⏳ [OTHER INCOME UPLOAD] Waiting for backend response...");
        const responseData = await response.json();

        console.log("📥 [OTHER INCOME UPLOAD] Backend response received:");
        console.log("   Status:", response.status);
        console.log("   Success:", response.ok);
        console.log("   Data:", responseData);

        if (!response.ok) {
          console.error(
            "❌ [OTHER INCOME UPLOAD] Upload failed:",
            responseData.message
          );
          throw new Error(responseData.message || "Upload failed");
        }

        console.log("✅ [OTHER INCOME UPLOAD] Upload completed successfully!");
        console.log("   Total rows:", responseData.summary?.totalRows);
        console.log("   Successful:", responseData.summary?.successfulRows);
        console.log("   Errors:", responseData.summary?.errorRows);

        setUploadResult(responseData);
        setFile(null);
        setLoading(false);
        
        // Reset file input
        const fileInput = document.getElementById(
          "file-input"
        ) as HTMLInputElement;
        if (fileInput) fileInput.value = "";
        
        return; // Exit early for other income processing
      }

      // ✅ NEW: For income tab, parse Excel file and process both sheets
      if (activeTab === "income") {
        console.log("🚀 [INCOME UPLOAD] Starting income upload process...");
        console.log("📁 [INCOME UPLOAD] File:", file.name);
        console.log("🏢 [INCOME UPLOAD] Selected Project ID:", selectedProject);

        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            console.log("📖 [INCOME UPLOAD] Reading file data...");
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: "binary" });

            console.log(
              "📊 [INCOME UPLOAD] Available sheets:",
              Object.keys(workbook.Sheets)
            );

            // Extract both sheets: SaleDeed and Receivables
            const saleDeedSheet =
              workbook.Sheets["SaleDeed"] ||
              workbook.Sheets["saledeed"] ||
              workbook.Sheets["Sale Deed"];
            const receivablesSheet =
              workbook.Sheets["Receivables"] ||
              workbook.Sheets["receivables"] ||
              workbook.Sheets["Receivable"];

            console.log(
              "✅ [INCOME UPLOAD] SaleDeed sheet found:",
              !!saleDeedSheet
            );
            console.log(
              "✅ [INCOME UPLOAD] Receivables sheet found:",
              !!receivablesSheet
            );

            if (!saleDeedSheet && !receivablesSheet) {
              throw new Error(
                'Excel file must contain "SaleDeed" and/or "Receivables" sheet. Found sheets: ' +
                  Object.keys(workbook.Sheets).join(", ")
              );
            }

            const incomeData: any[] = [];

            // ✅ Process SaleDeed sheet
            if (saleDeedSheet) {
              console.log("📄 [INCOME UPLOAD] Processing SaleDeed sheet...");
              const saleDeedData: any[][] = XLSX.utils.sheet_to_json(
                saleDeedSheet,
                { header: 1 }
              );
              const saleDeedRows = saleDeedData.slice(1); // Skip header
              console.log(
                `📝 [INCOME UPLOAD] SaleDeed rows (excluding header): ${saleDeedRows.length}`
              );

              let saleDeedCount = 0;
              for (const row of saleDeedRows) {
                const [
                  unitNo, // 0: Unit No (G-802 or 902)
                  incomeType, // 1: Income Type (Sale Deed)
                  amount, // 2: Amount
                  paymentMode, // 3: Payment Mode
                  paymentType, // 4: Payment Type (not used)
                  amountType, // 5: Amount type (not used)
                  incomeHeadName, // 6: income head name (not used)
                  dateReceived, // 7: Date received
                  bankName, // 8: Bank name
                  chequeNumber, // 9: Cheque Number
                  chequeDate, // 10: Cheque date (instrument date)
                  receivedFrom, // 11: Received From (not used)
                  receiptNumber, // 12: Receipt number (not used)
                  receiptUrl, // 13: Receipt url (not used)
                  referenceNumber, // 14: Reference number
                  narration, // 15: Narration (remarks)
                ] = row as any[];

                if (!unitNo || !amount || !dateReceived) continue; // Skip empty rows

                saleDeedCount++;
                incomeData.push({
                  unitNo,
                  incomeType: incomeType || "Sale Deed",
                  amount,
                  paymentMode,
                  dateReceived,
                  bankName,
                  chequeNumber,
                  instrumentDate: chequeDate, // Store cheque date as instrument date
                  referenceNumber,
                  remarks: narration, // Store narration as remarks
                  sheetType: "SaleDeed", // Mark as sale deed
                });
              }
              console.log(
                `✅ [INCOME UPLOAD] Parsed ${saleDeedCount} valid SaleDeed rows`
              );
            }

            // ✅ Process Receivables sheet
            if (receivablesSheet) {
              console.log("📄 [INCOME UPLOAD] Processing Receivables sheet...");
              const receivablesData: any[][] = XLSX.utils.sheet_to_json(
                receivablesSheet,
                { header: 1 }
              );
              const receivablesRows = receivablesData.slice(1); // Skip header
              console.log(
                `📝 [INCOME UPLOAD] Receivables rows (excluding header): ${receivablesRows.length}`
              );

              let receivablesCount = 0;
              for (const row of receivablesRows) {
                const [
                  no, // 0: No (not used)
                  date, // 1: Date
                  unitNo, // 2: Unit No
                  flatShop, // 3: Flat/shop (not used)
                  cr, // 4: Cr (amount)
                ] = row as any[];

                if (!unitNo || !cr || !date) continue; // Skip empty rows

                receivablesCount++;
                incomeData.push({
                  unitNo,
                  incomeType: "Receivables", // Fixed type for receivables
                  amount: cr,
                  dateReceived: date,
                  sheetType: "Receivables", // Mark as receivables (will be encrypted)
                  paymentMode: "Receivables", // To trigger receivables detection
                });
              }
              console.log(
                `✅ [INCOME UPLOAD] Parsed ${receivablesCount} valid Receivables rows`
              );
            }

            console.log(
              `📊 [INCOME UPLOAD] Total income records to upload: ${incomeData.length}`
            );
            console.log(
              "📋 [INCOME UPLOAD] Sample data (first 2 records):",
              incomeData.slice(0, 2)
            );

            // Send combined data to backend
            const API_BASE_URL =
              process.env.REACT_APP_API_URL || "http://localhost:5010/api";
            const token = getToken();

            console.log(
              "🌐 [INCOME UPLOAD] API Endpoint:",
              `${API_BASE_URL}/upload-data/income`
            );
            console.log(
              "📤 [INCOME UPLOAD] Sending to backend:",
              incomeData.length,
              "rows"
            );

            const response = await fetch(`${API_BASE_URL}/upload-data/income`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "X-Hidden-Mode": "true",
              },
              body: JSON.stringify({
                incomeData,
                projectId: selectedProject,
              }),
            });

            console.log("⏳ [INCOME UPLOAD] Waiting for backend response...");
            const responseData = await response.json();

            console.log("📥 [INCOME UPLOAD] Backend response received:");
            console.log("   Status:", response.status);
            console.log("   Success:", response.ok);
            console.log("   Data:", responseData);

            if (!response.ok) {
              console.error(
                "❌ [INCOME UPLOAD] Upload failed:",
                responseData.message
              );
              throw new Error(responseData.message || "Upload failed");
            }

            console.log("✅ [INCOME UPLOAD] Upload completed successfully!");
            console.log("   Total rows:", responseData.summary?.totalRows);
            console.log("   Successful:", responseData.summary?.successfulRows);
            console.log("   Errors:", responseData.summary?.errorRows);

            setUploadResult(responseData);
            setFile(null);
            // Reset file input
            const fileInput = document.getElementById(
              "file-input"
            ) as HTMLInputElement;
            if (fileInput) fileInput.value = "";
          } catch (error: any) {
            console.error("❌ [INCOME UPLOAD] Excel parsing error:", error);
            setError(error.message || "Failed to parse Excel file");
          } finally {
            setLoading(false);
          }
        };

        reader.readAsBinaryString(file);
        return; // Exit early for income processing
      }

      // ✅ Original upload logic for booking, unit, expense
      const formData = new FormData();
      formData.append("file", file);

      // Add projectId for booking, unit, and expense uploads
      if (
        (activeTab === "booking" ||
          activeTab === "unit" ||
          activeTab === "expense") &&
        selectedProject
      ) {
        formData.append("projectId", selectedProject);
      }

      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const token = getToken();

      const response = await fetch(`${API_BASE_URL}/upload-data/${activeTab}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Hidden-Mode": "true",
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Upload failed");
      }

      setUploadResult(data);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById(
        "file-input"
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    } catch (error: any) {
      console.error("Upload error:", error);
      setError(error.message || "Failed to upload file");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse customer name with title support
  const parseCustomerName = (
    fullName: string
  ): {
    title: string;
    firstName: string;
    middleName: string;
    lastName: string;
  } => {
    if (!fullName || typeof fullName !== "string") {
      return { title: "", firstName: "", middleName: "", lastName: "" };
    }

    const trimmed = fullName.trim();
    const parts = trimmed.split(/\s+/).filter((p) => p.length > 0); // Split by spaces and remove empty

    const commonTitles = [
      "Mr",
      "Mrs",
      "Ms",
      "Miss",
      "Dr",
      "Prof",
      "Er",
      "Adv",
      "CA",
      "Shri",
      "Smt",
    ];

    let title = "";
    let nameParts = [...parts];

    // Check if first part is a title
    if (
      parts.length > 0 &&
      commonTitles.some((t) => t.toLowerCase() === parts[0].toLowerCase())
    ) {
      title = parts[0];
      nameParts = parts.slice(1);
    }

    // Parse remaining name parts
    if (nameParts.length === 1) {
      // Only first name
      return { title, firstName: nameParts[0], middleName: "", lastName: "" };
    } else if (nameParts.length === 2) {
      // First name and last name
      return {
        title,
        firstName: nameParts[0],
        middleName: "",
        lastName: nameParts[1],
      };
    } else if (nameParts.length >= 3) {
      // First name, middle name(s), and last name
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      const middleName = nameParts.slice(1, -1).join(" ");
      return { title, firstName, middleName, lastName };
    }

    return { title, firstName: trimmed, middleName: "", lastName: "" };
  };

  // Helper function to normalize mobile numbers (convert | to /)
  const normalizeMobileNumber = (mobile: string): string => {
    if (!mobile || typeof mobile !== "string") {
      return "";
    }
    // Replace pipe | with slash /
    return mobile.trim().replace(/\|/g, "/");
  };

  // Helper function to parse unit number (similar to Units.tsx)
  const parseUnitNumber = (
    combined: string
  ): { wing: string | null; unitNo: string } => {
    if (!combined || typeof combined !== "string") {
      return { wing: null, unitNo: "" };
    }

    const trimmed = combined.trim();

    // Check if it contains a hyphen (e.g., "B-101")
    if (trimmed.includes("-")) {
      const parts = trimmed.split("-");
      if (parts.length === 2) {
        return {
          wing: parts[0].trim().toUpperCase(),
          unitNo: parts[1].trim(),
        };
      }
    }

    // No hyphen means it's a shop or unit without wing
    return {
      wing: null,
      unitNo: trimmed,
    };
  };

  const downloadTemplate = () => {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL || "http://localhost:5010/api";
    const token = getToken();

    const link = document.createElement("a");
    link.href = `${API_BASE_URL}/upload-data/template/${activeTab}?token=${token}`;
    link.download = `${activeTab}_template.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="upload-data-container">
      <div className="page-header">
        <h1>📤 Upload Data</h1>
        <p>Upload booking and income data from Excel files</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "booking" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("booking");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          📋 Upload Booking Data
        </button>
        <button
          className={`tab-button ${activeTab === "unit" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("unit");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          🏠 Upload Unit Data
        </button>
        <button
          className={`tab-button ${activeTab === "income" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("income");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          💰 Upload Income Data
        </button>
        <button
          className={`tab-button ${activeTab === "expense" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("expense");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          💸 Upload Expense Data
        </button>
        <button
          className={`tab-button ${activeTab === "otherIncome" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("otherIncome");
            setFile(null);
            setError(null);
            setUploadResult(null);
          }}
        >
          💰 Upload Other Income
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="upload-content">
        {activeTab === "booking" && (
          <div className="upload-section">
            <div className="section-header">
              <h2>Upload Booking Data</h2>
              <p>
                Upload booking information from Excel/CSV files with customer
                details, units, and amounts.
              </p>
            </div>

            {/* Project Selection */}
            <div className="project-selection">
              <h3>🏢 Select Project</h3>
              {loadingProjects ? (
                <div className="loading-spinner">Loading projects...</div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="project-select"
                  required
                >
                  <option value="">-- Select a Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
              {!selectedProject && (
                <p className="selection-warning">
                  ⚠️ Please select a project before uploading booking data
                </p>
              )}
            </div>

            <div className="template-section">
              <h3>📝 Expected Format</h3>
              <div className="format-info">
                <p>
                  Your Excel file should contain the following columns{" "}
                  <strong>in this exact order</strong>:
                </p>
                <ol>
                  <li>
                    <strong>Project Unit</strong> - Unit reference (e.g., B-102
                    or 103 for shops)
                  </li>
                  <li>
                    <strong>Booking Date</strong> - Date in DD/MM/YYYY format
                  </li>
                  <li>
                    <strong>Other Work Amount</strong> - Additional work charges
                    (encrypted storage)
                  </li>
                  <li>
                    <strong>Payment Terms</strong> - Payment terms description
                  </li>
                  <li>
                    <strong>Address</strong> - Customer address
                  </li>
                  <li>
                    <strong>Customer Name 1</strong> - Primary customer
                    (supports Title First Middle Last format)
                  </li>
                  <li>
                    <strong>Customer Mobile 1</strong> - Primary mobile (use /
                    to separate multiple numbers)
                  </li>
                  <li>
                    <strong>Customer Name 2</strong> - Second customer
                    (optional, same name format)
                  </li>
                  <li>
                    <strong>Customer Mobile 2</strong> - Second mobile
                    (optional, use / for multiple)
                  </li>
                  <li>
                    <strong>Remarks</strong> - Additional notes (optional)
                  </li>
                </ol>
                <div className="format-note">
                  <strong>📌 Name Format Examples:</strong>
                  <ul style={{ marginTop: "8px" }}>
                    <li>"Rajesh Kumar" → First: Rajesh, Last: Kumar</li>
                    <li>
                      "Rajesh Kumar Shah" → First: Rajesh, Middle: Kumar, Last:
                      Shah
                    </li>
                    <li>
                      "Mr Rajesh Kumar Shah" → Title: Mr, First: Rajesh, Middle:
                      Kumar, Last: Shah
                    </li>
                  </ul>
                  <strong>📱 Mobile Format Examples:</strong>
                  <ul style={{ marginTop: "8px" }}>
                    <li>"9876543210" → Single number</li>
                    <li>
                      "9876543210|9988776655" → Converted to
                      "9876543210/9988776655"
                    </li>
                    <li>
                      "9876543210/9988776655" → Multiple numbers (pipe |
                      automatically converted to /)
                    </li>
                  </ul>
                </div>
              </div>
              <button className="template-button" onClick={downloadTemplate}>
                📄 Download Template
              </button>
            </div>
          </div>
        )}

        {activeTab === "unit" && (
          <div className="upload-section">
            <div className="section-header">
              <h2>Upload Unit Data</h2>
              <p>
                Upload unit information from Excel files with unit details,
                pricing, and availability.
              </p>
            </div>

            {/* Project Selection */}
            <div className="project-selection">
              <h3>🏢 Select Project</h3>
              {loadingProjects ? (
                <div className="loading-spinner">Loading projects...</div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="project-select"
                  required
                >
                  <option value="">-- Select a Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
              {!selectedProject && (
                <p className="selection-warning">
                  ⚠️ Please select a project before uploading unit data
                </p>
              )}
            </div>

            <div className="template-section">
              <h3>📝 Expected Format</h3>
              <div className="format-info">
                <p>Your Excel file should contain the following columns:</p>
                <ul>
                  <li>
                    <strong>Unit Number</strong> - Unit reference (e.g., A-101,
                    S-01)
                  </li>
                  <li>
                    <strong>Wing</strong> - Building wing (e.g., A, B, C) -
                    leave empty for shops
                  </li>
                  <li>
                    <strong>Floor Number</strong> - Floor level (optional)
                  </li>
                  <li>
                    <strong>Unit Type</strong> - Type (e.g., 2BHK, 3BHK, Shop)
                  </li>
                  <li>
                    <strong>Area (sq ft)</strong> - Unit area in square feet
                  </li>
                  <li>
                    <strong>Price</strong> - Sale deed amount
                  </li>
                  <li>
                    <strong>Status</strong> - Current status (available, sold,
                    booked, etc.)
                  </li>
                </ul>
              </div>
              <button className="template-button" onClick={downloadTemplate}>
                📄 Download Template
              </button>
            </div>
          </div>
        )}

        {activeTab === "income" && (
          <div className="upload-section">
            <div className="section-header">
              <h2>Upload Income Data</h2>
              <p>
                Upload income transaction data from Excel files with TWO sheets:
                "SaleDeed" and "Receivables".
              </p>
            </div>

            {/* Project Selection */}
            <div className="project-selection">
              <h3>🏢 Select Project</h3>
              {loadingProjects ? (
                <div className="loading-spinner">Loading projects...</div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="project-select"
                  required
                >
                  <option value="">-- Select a Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
              {!selectedProject && (
                <p className="selection-warning">
                  ⚠️ Please select a project before uploading income data
                </p>
              )}
            </div>

            <div className="template-section">
              <h3>📝 Expected Format</h3>
              <div className="format-info">
                <p>
                  <strong>Your Excel file must have TWO sheets:</strong>
                </p>

                <div style={{ marginBottom: "20px" }}>
                  <h4>📄 Sheet 1: "SaleDeed" (Sale Deed Payments)</h4>
                  <p>Columns in order:</p>
                  <ul>
                    <li>
                      <strong>Unit No</strong> - G-802 or 902 (with/without
                      wing)
                    </li>
                    <li>
                      <strong>Income Type</strong> - "Sale Deed" (will be stored
                      in DB)
                    </li>
                    <li>
                      <strong>Amount</strong> - Payment amount
                    </li>
                    <li>
                      <strong>Payment Mode</strong> - Cash, Cheque, UPI, etc.
                    </li>
                    <li>
                      <strong>Payment Type</strong> - (Not used, can be empty)
                    </li>
                    <li>
                      <strong>Amount type</strong> - (Not used, can be empty)
                    </li>
                    <li>
                      <strong>Income head name</strong> - (Not used, can be
                      empty)
                    </li>
                    <li>
                      <strong>Date received</strong> - Date in DD/MM/YYYY format
                    </li>
                    <li>
                      <strong>Bank name</strong> - Bank name for cheque/transfer
                    </li>
                    <li>
                      <strong>Cheque Number</strong> - If payment is by cheque
                    </li>
                    <li>
                      <strong>Cheque date</strong> - Cheque/instrument date
                    </li>
                    <li>
                      <strong>Received From</strong> - (Not used, can be empty)
                    </li>
                    <li>
                      <strong>Receipt number</strong> - (Not used, can be empty)
                    </li>
                    <li>
                      <strong>Receipt url</strong> - (Not used, can be empty)
                    </li>
                    <li>
                      <strong>Reference number</strong> - Transaction reference
                      (combined with cheque number)
                    </li>
                    <li>
                      <strong>Narration</strong> - Remarks/notes about the
                      payment
                    </li>
                  </ul>
                </div>

                <div>
                  <h4>💰 Sheet 2: "Receivables" (Other Income - ENCRYPTED)</h4>
                  <p>Columns in order:</p>
                  <ul>
                    <li>
                      <strong>No</strong> - (Not used, can be serial number)
                    </li>
                    <li>
                      <strong>Date</strong> - Income date in DD/MM/YYYY format
                    </li>
                    <li>
                      <strong>Unit No</strong> - With or without wing (G-802 or
                      902)
                    </li>
                    <li>
                      <strong>Flat/shop</strong> - (Not used, can be empty)
                    </li>
                    <li>
                      <strong>Cr</strong> - Credit amount (income amount)
                    </li>
                  </ul>
                  <p className="format-note" style={{ marginTop: '10px', color: '#666', fontSize: '0.9em' }}>
                    <strong>Note:</strong> Customer name is automatically fetched from the booking's primary applicant. Do not include a Name column.
                  </p>
                </div>

                <div className="format-note">
                  <strong>⚠️ Important Notes:</strong>
                  <ul>
                    <li>
                      ✅ SaleDeed sheet: Regular income linked to bookings
                    </li>
                    <li>
                      🔒 Receivables sheet: Encrypted and stored in systems
                      database with NEW encryption key
                    </li>
                    <li>Both sheets are processed together in one upload</li>
                    <li>
                      Sheet names must be exactly "SaleDeed" and "Receivables"
                      (case insensitive)
                    </li>
                  </ul>
                </div>
              </div>
              <button className="template-button" onClick={downloadTemplate}>
                📄 Download Template
              </button>
            </div>
          </div>
        )}

        {activeTab === "expense" && (
          <div className="upload-section">
            <div className="section-header">
              <h2>Upload Expense Data</h2>
              <p>
                Upload expense transaction data from Excel files. Expense heads
                will be created automatically if they don't exist.
              </p>
            </div>

            {/* Project Selection */}
            <div className="project-selection">
              <h3>🏢 Select Project</h3>
              {loadingProjects ? (
                <div className="loading-spinner">Loading projects...</div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="project-select"
                  required
                >
                  <option value="">-- Select a Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
              {!selectedProject && (
                <p className="selection-warning">
                  ⚠️ Please select a project before uploading expense data
                </p>
              )}
            </div>

            <div className="template-section">
              <h3>📝 Expected Format</h3>
              <div className="format-info">
                <p>
                  Your Excel file should contain the following columns in order:
                </p>
                <ul>
                  <li>
                    <strong>No</strong> - Row number (optional, will be ignored)
                  </li>
                  <li>
                    <strong>Date</strong> - Expense date in DD/MM/YYYY format
                  </li>
                  <li>
                    <strong>Narration</strong> - Description of the expense
                  </li>
                  <li>
                    <strong>Exp Head</strong> - Expense head/category (will be
                    created if doesn't exist)
                  </li>
                  <li>
                    <strong>Voucher No</strong> - Voucher number (can be
                    "Direct" or any number)
                  </li>
                  <li>
                    <strong>Amount</strong> - Expense amount
                  </li>
                </ul>
                <div className="format-note">
                  <strong>Note:</strong>
                  <br />• If the expense head doesn't exist, it will be created
                  automatically
                  <br />• All expense data is encrypted and stored securely
                  <br />• Expenses are auto-approved upon upload
                </div>
              </div>
              <div className="sample-format">
                <h4>Sample Data Format:</h4>
                <div className="sample-table">
                  <table>
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>Date</th>
                        <th>Narration</th>
                        <th>Exp Head</th>
                        <th>Voucher No</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>10/11/2022</td>
                        <td>Block - 9 - Akatrikaran 7/12 ( Jamin Pete )</td>
                        <td>Land & Legal</td>
                        <td>Direct</td>
                        <td>4000.00</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>17/11/2022</td>
                        <td>Abhipray Paper kadhavya</td>
                        <td>Land & Legal</td>
                        <td>Direct</td>
                        <td>150.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <button className="template-button" onClick={downloadTemplate}>
                📄 Download Template
              </button>
            </div>
          </div>
        )}

        {activeTab === "otherIncome" && (
          <div className="upload-section">
            <div className="section-header">
              <h2>Upload Other Income Data</h2>
              <p>
                Upload other income data (miscellaneous income not related to bookings) from Excel files.
              </p>
            </div>

            {/* Project Selection */}
            <div className="project-selection">
              <h3>🏢 Select Project</h3>
              {loadingProjects ? (
                <div className="loading-spinner">Loading projects...</div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="project-select"
                  required
                >
                  <option value="">-- Select a Project --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
              {!selectedProject && (
                <p className="selection-warning">
                  ⚠️ Please select a project before uploading other income data
                </p>
              )}
            </div>

            <div className="template-section">
              <h3>📝 Expected Format</h3>
              <div className="format-info">
                <p>
                  Your Excel file should contain the following columns in order:
                </p>
                <ul>
                  <li>
                    <strong>Date</strong> - Income date (supports: DD/MM/YYYY, MM/DD/YYYY, D/M/YYYY, M/D/YYYY)
                  </li>
                  <li>
                    <strong>Income Head</strong> - Name of the income head/category (will be created if doesn't exist)
                  </li>
                  <li>
                    <strong>Narration</strong> - Description/notes about the income (optional)
                  </li>
                  <li>
                    <strong>Amount</strong> - Income amount
                  </li>
                </ul>
                <div className="format-note">
                  <strong>🔒 Security Note:</strong>
                  <br />• All other income data is encrypted and stored securely in systems database
                  <br />• Income heads will be created automatically if they don't exist
                  <br />• Other income entries are auto-approved upon upload
                  <br />• This is for miscellaneous income NOT related to unit bookings
                </div>
              </div>
              <div className="sample-format">
                <h4>Sample Data Format:</h4>
                <div className="sample-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Income Head</th>
                        <th>Narration</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>01/11/2025</td>
                        <td>Rental Income</td>
                        <td>Office rent for November</td>
                        <td>25000</td>
                      </tr>
                      <tr>
                        <td>05/11/2025</td>
                        <td>Interest Income</td>
                        <td>Interest on FD</td>
                        <td>5000</td>
                      </tr>
                      <tr>
                        <td>10/11/2025</td>
                        <td>Miscellaneous</td>
                        <td>Parking charges collection</td>
                        <td>1200</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <button className="template-button" onClick={downloadTemplate}>
                📄 Download Template
              </button>
            </div>
          </div>
        )}

        {/* File Upload Section */}
        <div className="file-upload-section">
          <h3>📁 Select File</h3>
          <div className="file-input-container">
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="file-input" className="file-input-label">
              {file ? file.name : "Choose Excel or CSV file..."}
            </label>
          </div>

          {file && (
            <div className="file-info">
              <p>
                ✅ Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            </div>
          )}

          <button
            className="upload-button"
            onClick={handleUpload}
            disabled={
              !file ||
              loading ||
              ((activeTab === "booking" ||
                activeTab === "unit" ||
                activeTab === "income" ||
                activeTab === "expense" ||
                activeTab === "otherIncome") &&
                !selectedProject)
            }
          >
            {loading ? "⏳ Uploading..." : "🚀 Upload Data"}
          </button>

          {(activeTab === "booking" ||
            activeTab === "unit" ||
            activeTab === "income" ||
            activeTab === "expense" ||
            activeTab === "otherIncome") &&
            !selectedProject && (
              <p className="upload-warning">
                Please select a project before uploading {activeTab === "otherIncome" ? "other income" : activeTab} data
              </p>
            )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <h4>❌ Upload Error</h4>
            <p>{error}</p>
          </div>
        )}

        {/* Success Result */}
        {uploadResult && (
          <div className="success-message">
            <h4>
              ✅ Upload{" "}
              {(uploadResult.summary?.errorRows || uploadResult.data?.failureCount || uploadResult.failureCount || 0) > 0
                ? "Completed with Errors"
                : "Successful"}
            </h4>
            <div className="result-details">
              <p>
                <strong>Total Records:</strong>{" "}
                {uploadResult.summary?.totalRows ||
                  uploadResult.data?.totalRecords ||
                  uploadResult.totalRecords ||
                  0}
              </p>
              <p>
                <strong>Successfully Processed:</strong>{" "}
                {uploadResult.summary?.successfulRows ||
                  uploadResult.data?.successCount ||
                  uploadResult.successCount ||
                  0}
              </p>
              <p>
                <strong>Failed:</strong>{" "}
                {uploadResult.summary?.errorRows ||
                  uploadResult.data?.failureCount ||
                  uploadResult.failureCount ||
                  0}
              </p>

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="error-details">
                  <h5>Processing Errors ({uploadResult.errors.length}):</h5>
                  <p
                    style={{
                      color: "#d32f2f",
                      fontSize: "13px",
                      marginBottom: "12px",
                    }}
                  >
                    💡 <strong>Tip:</strong> Most errors occur when units don't
                    have active bookings (required for receivables) or the unit
                    doesn't exist in the selected project.
                  </p>
                  <button
                    onClick={() => {
                      const csvContent =
                        "data:text/csv;charset=utf-8," +
                        "Row,Error,Unit No,Sheet Type\n" +
                        uploadResult.errors
                          .map(
                            (e: any) =>
                              `${e.row},"${e.error}",${e.data?.unitNo || ""},${
                                e.data?.sheetType || ""
                              }`
                          )
                          .join("\n");
                      const link = document.createElement("a");
                      link.setAttribute("href", encodeURI(csvContent));
                      link.setAttribute(
                        "download",
                        `income_upload_errors_${new Date().getTime()}.csv`
                      );
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    style={{
                      padding: "8px 16px",
                      marginBottom: "12px",
                      backgroundColor: "#1976d2",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    📥 Download Error Report (CSV)
                  </button>
                  {/* Error Analysis & Grouping */}
                  <div
                    style={{
                      borderTop: "1px solid #eee",
                      paddingTop: 12,
                      marginTop: 12,
                    }}
                  >
                    <h5>🔎 Error Analysis</h5>
                    <p style={{ color: "#555", fontSize: 13 }}>
                      Grouped by error message. Use these quick exports to fix
                      the most common issues first.
                    </p>
                    {errorGroups.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {errorGroups.map((g, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: 10,
                              background: "#fafafa",
                              border: "1px solid #eee",
                              borderRadius: 6,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {g.message}
                                </div>
                                <div style={{ fontSize: 13, color: "#333" }}>
                                  {g.count} row{g.count !== 1 ? "s" : ""}
                                  {g.sheetTypes.length > 0 && (
                                    <span style={{ color: "#777" }}>
                                      {" "}
                                      • Sheets: {g.sheetTypes.join(", ")}
                                    </span>
                                  )}
                                </div>
                                {g.sampleUnits.length > 0 && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: "#666",
                                      marginTop: 4,
                                    }}
                                  >
                                    Sample units: {g.sampleUnits.join(", ")}
                                    {g.sampleUnits.length === 10 ? "…" : ""}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  onClick={() => {
                                    const csvContent =
                                      "data:text/csv;charset=utf-8," +
                                      "Row,Error,Unit No,Sheet Type\n" +
                                      g.rows
                                        .map(
                                          (e: any) =>
                                            `${e.row},"${
                                              e.error || e.message
                                            }",${e.data?.unitNo || ""},${
                                              e.data?.sheetType || ""
                                            }`
                                        )
                                        .join("\n");
                                    const link = document.createElement("a");
                                    link.setAttribute(
                                      "href",
                                      encodeURI(csvContent)
                                    );
                                    link.setAttribute(
                                      "download",
                                      `errors_group_${idx}_${Date.now()}.csv`
                                    );
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                  style={{
                                    padding: "6px 10px",
                                    background: "#455a64",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                  }}
                                >
                                  Export this group
                                </button>
                                {/* Special export for missing units within selected project */}
                                {/Unit not found in selected project/i.test(
                                  g.message
                                ) && (
                                  <button
                                    onClick={() => {
                                      const uniqueUnits = Array.from(
                                        new Set(
                                          g.rows
                                            .map((e: any) => e?.data?.unitNo)
                                            .filter(Boolean)
                                        )
                                      );
                                      const csvContent =
                                        "data:text/csv;charset=utf-8,Unit No,Selected Project ID\n" +
                                        uniqueUnits
                                          .map(
                                            (u: string) =>
                                              `${u},${selectedProject}`
                                          )
                                          .join("\n");
                                      const link = document.createElement("a");
                                      link.setAttribute(
                                        "href",
                                        encodeURI(csvContent)
                                      );
                                      link.setAttribute(
                                        "download",
                                        `missing_units_project_${selectedProject}_${Date.now()}.csv`
                                      );
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    style={{
                                      padding: "6px 10px",
                                      background: "#d32f2f",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 4,
                                      cursor: "pointer",
                                    }}
                                  >
                                    Export missing units
                                  </button>
                                )}
                              </div>
                            </div>
                            {/* Targeted hints */}
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: "#444",
                              }}
                            >
                              {/Unit not found in selected project/i.test(
                                g.message
                              ) && (
                                <div>
                                  Hint: These units don’t exist in the selected
                                  project. Verify the project selection for
                                  those rows or add the missing units to this
                                  project.
                                </div>
                              )}
                              {/no active booking/i.test(g.message) && (
                                <div>
                                  Hint: Receivables can be stored without active
                                  bookings (we create a placeholder). If you
                                  still see this, ensure unit lookup succeeded
                                  first.
                                </div>
                              )}
                              {/invalid|missing|required/i.test(g.message) && (
                                <div>
                                  Hint: Check for empty or malformed columns
                                  (amount, dates). Ensure DD/MM/YYYY dates and
                                  numeric amounts.
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <ul style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {uploadResult.errors.map((error: any, index: number) => (
                      <li
                        key={index}
                        style={{ marginBottom: "8px", fontSize: "14px" }}
                      >
                        <strong>Row {error.row}:</strong>{" "}
                        {error.error || error.message}
                        {error.data?.unitNo && (
                          <span style={{ color: "#666", marginLeft: "8px" }}>
                            (Unit: {error.data.unitNo})
                          </span>
                        )}
                        {error.data?.sheetType && (
                          <span style={{ color: "#999", marginLeft: "4px" }}>
                            [{error.data.sheetType}]
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uploadResult.warnings && uploadResult.warnings.length > 0 && (
                <div className="warning-details">
                  <h5>Warnings:</h5>
                  <ul>
                    {uploadResult.warnings.map(
                      (warning: any, index: number) => (
                        <li key={index}>
                          Row {warning.row}: {warning.message}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadData;
